import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  InputBase,
  IconButton,
  Typography,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
} from '@mui/material';
import { CONTAINER_SX } from '../../style/containers';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Dns as DnsIcon,
} from '@mui/icons-material';
import { getSuggestions, trackSuggestionClick } from '../../api/products';
import { withTrailingSlash } from '../../utils/url';

const BRAND_BLUE = '#005DAA';
const BRAND_LIGHT_BLUE = '#178ACB';
const BORDER_COLOR = '#e9edf2';
const FONT = 'Manrope, sans-serif';

// Builds full hierarchical product URL — mirrors CategoryProductsPage / ProductDetailPage logic.
// Root categories (parent_id == null) stay at /products; non-root ancestors build the path.
// Shared ancestor-walk logic — returns [non-root ancestors] for a given category slug
const getAncestors = (categorySlug, catBySlug, catById) => {
  const found = catBySlug[categorySlug];
  if (!found || !found.parent_id) return { found, ancestors: [] };
  const ancestors = [];
  let cur = found;
  while (cur?.parent_id) {
    const parent = catById[String(cur.parent_id)];
    if (!parent) break;
    ancestors.unshift(parent);
    cur = parent;
  }
  return { found, ancestors: ancestors.filter((a) => a.parent_id != null) };
};

// Full hierarchical product URL: /products/{cat-chain}/{productId}
const buildProductPath = (productId, categorySlug, catBySlug, catById) => {
  if (!categorySlug || !catBySlug) return withTrailingSlash(`/products/${productId}`);
  const { found, ancestors } = getAncestors(categorySlug, catBySlug, catById);
  if (!found || !found.parent_id) return withTrailingSlash(`/products/${productId}`);
  const segments = [...ancestors.map((a) => a.slug || a.id), found.slug || found.id, productId];
  return withTrailingSlash('/products/' + segments.join('/'));
};

// Full hierarchical category URL: /products/{cat-chain}
const buildCategoryPath = (categorySlug, catBySlug, catById) => {
  if (!categorySlug || !catBySlug) return withTrailingSlash(`/categories/${categorySlug}`);
  const { found, ancestors } = getAncestors(categorySlug, catBySlug, catById);
  if (!found) return withTrailingSlash(`/categories/${categorySlug}`);
  if (!found.parent_id) return withTrailingSlash('/products'); // root maps to /products
  const segments = [...ancestors.map((a) => a.slug || a.id), found.slug || found.id];
  return withTrailingSlash('/products/' + segments.join('/'));
};

const SearchModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);

  // Categories from Redux (loaded on app start) — used to build full product URLs.
  // state.categories.list may be the raw axios response {data: Array(198)} or a plain array —
  // normalize exactly as ProductDetailPage does.
  const reduxCategories = useSelector((state) => state.categories.list);
  const allCategories = useMemo(() => {
    if (Array.isArray(reduxCategories)) return reduxCategories;
    if (Array.isArray(reduxCategories?.data?.data)) return reduxCategories.data.data;
    if (Array.isArray(reduxCategories?.data)) return reduxCategories.data;
    return [];
  }, [reduxCategories]);
  const catBySlug = useMemo(
    () => Object.fromEntries(allCategories.map((c) => [c.slug, c])),
    [allCategories]
  );
  const catById = useMemo(
    () => Object.fromEntries(allCategories.map((c) => [String(c.id), c])),
    [allCategories]
  );

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  // Sync scroll lock and local animations
  // Use event-based scroll blocking instead of overflow:hidden — overflow:hidden
  // kills position:sticky on the header, making it disappear when search opens.
  useEffect(() => {
    if (open) {
      const prevent = (e) => {
        if (e.target.closest('[data-search-scroll]')) return;
        e.preventDefault();
      };
      document.addEventListener('wheel', prevent, { passive: false });
      document.addEventListener('touchmove', prevent, { passive: false });
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 150);
      return () => {
        document.removeEventListener('wheel', prevent);
        document.removeEventListener('touchmove', prevent);
      };
    }
    // Closed (including via navigation) — clear the query/results so the
    // next open starts fresh instead of showing the last search.
    setQuery('');
    setCategories([]);
    setProducts([]);
    setTotalCount(0);
    setHasSearched(false);
  }, [open]);

  // Handle Search Execution
  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setCategories([]);
      setProducts([]);
      setTotalCount(0);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const { products: apiResults, total } = await getSuggestions(searchQuery.trim());

      if (apiResults && apiResults.length > 0) {
        const categoryMap = {};
        const parsedProducts = apiResults.map((prod) => {
          const categoryName = prod.category?.name || 'Other';
          const categorySlug = prod.category?.slug || '';

          if (!categoryMap[categoryName]) {
            categoryMap[categoryName] = {
              name: categoryName,
              slug: categorySlug,
              count: 0,
            };
          }
          categoryMap[categoryName].count += 1;

          return {
            id: prod.id,
            name: prod.translations?.[0]?.name || prod.name || 'Product',
            category: categoryName,
            categorySlug: categorySlug,
            image: prod.image?.path || '',
          };
        });

        setCategories(Object.values(categoryMap));
        setProducts(parsedProducts.slice(0, 8));
        setTotalCount(total);
      } else {
        setCategories([]);
        setProducts([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.warn('Suggestions API failed:', error);
      setCategories([]);
      setProducts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      performSearch(val);
    }, 300);
  };

  const handleClearOrClose = () => {
    if (query) {
      setQuery('');
      setCategories([]);
      setProducts([]);
      setTotalCount(0);
      setHasSearched(false);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } else {
      onClose();
    }
  };

  const handleProductClick = (productId, categorySlug) => {
    if (query.trim()) {
      trackSuggestionClick(query.trim());
    }
    navigate(buildProductPath(productId, categorySlug, catBySlug, catById));
    onClose();
  };

  const handleCategoryClick = (slug) => {
    navigate(buildCategoryPath(slug, catBySlug, catById));
    onClose();
  };

  const handleViewAll = () => {
    const params = new URLSearchParams();
    params.append('search', query.trim());
    navigate(withTrailingSlash(`/search?${params.toString()}`));
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && query.trim()) {
      handleViewAll();
    }
  };

  if (!open) return null;

  return (
    <>
      {/* ── Search bar — slides in just below the header ── */}
      <Box
        sx={{
          position: 'fixed',
          top: 90,
          left: 0,
          right: 0,
          height: 64,
          bgcolor: 'rgba(255,255,255,0.90)',
          zIndex: 1201,
          display: 'flex',
          alignItems: 'center',
          animation: 'searchIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          '@keyframes searchIn': {
            from: { opacity: 0, transform: 'translateY(-10px)' },
            to:   { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <Container maxWidth={false} sx={CONTAINER_SX}>
          {/* Pill-shaped input wrapper */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#fff',
              border: `1.5px solid ${BRAND_LIGHT_BLUE}`,
              borderRadius: '999px',
              px: 2,
              py: 0.75,
              boxShadow: '0 2px 12px rgba(23,138,203,0.12)',
            }}
          >
            <SearchIcon sx={{ color: BRAND_LIGHT_BLUE, fontSize: 20, flexShrink: 0 }} />
            <InputBase
              inputRef={inputRef}
              placeholder="Search products, categories, gateways..."
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              sx={{
                flex: 1,
                fontSize: '13px',
                fontFamily: FONT,
                fontWeight: 500,
                color: '#1e293b',
                '& input::placeholder': { color: '#94a3b8', opacity: 1, fontFamily: FONT, fontSize: '13px' },
              }}
            />
            {loading && (
              <CircularProgress size={16} sx={{ color: BRAND_LIGHT_BLUE, flexShrink: 0 }} />
            )}
            <IconButton
              onClick={handleClearOrClose}
              size="small"
              aria-label={query ? 'Clear search' : 'Close search'}
              sx={{
                flexShrink: 0,
                color: '#94a3b8',
                p: 0.5,
                '&:hover': { color: BRAND_BLUE, bgcolor: 'transparent' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* ── Backdrop — click outside to close ── */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 154,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(15, 23, 42, 0.35)',
          zIndex: 1200,
        }}
      />

      {/* ── Results dropdown — anchored just below the search bar ── */}
      {hasSearched && (
        <Box
          sx={{
            position: 'fixed',
            top: 154,
            left: 0,
            right: 0,
            zIndex: 1201,
            px: CONTAINER_SX.px,
            animation: 'resultsIn 0.18s ease-out',
            '@keyframes resultsIn': {
              from: { opacity: 0, transform: 'translateY(-6px)' },
              to:   { opacity: 1, transform: 'translateY(0)' },
            },
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '0 0 12px 12px',
              boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
              border: `1px solid ${BORDER_COLOR}`,
              borderTop: 'none',
              overflow: 'hidden',
              maxHeight: 'calc(80vh - 90px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Scrollable results list */}
            <Box data-search-scroll sx={{ overflowY: 'auto', flex: 1 }}>
              {/* Category Group */}
              {categories.length > 0 && (
                <Box sx={{ pt: 1.5, pb: 0.5, px: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '10px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: '#94a3b8',
                      letterSpacing: '0.08em',
                      mb: 0.25,
                    }}
                  >
                    CATEGORY
                  </Typography>
                  <List disablePadding>
                    {categories.map((cat) => (
                      <ListItemButton
                        key={cat.slug || cat.name}
                        onClick={() => handleCategoryClick(cat.slug)}
                        sx={{
                          py: 0.4,
                          px: 0.75,
                          borderRadius: '6px',
                          '&:hover': {
                            bgcolor: '#f0f7ff',
                            '& .cat-name': { color: BRAND_LIGHT_BLUE },
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              className="cat-name"
                              sx={{
                                fontFamily: FONT,
                                fontSize: '13px',
                                fontWeight: 600,
                                color: BRAND_BLUE,
                                transition: 'color 0.15s',
                              }}
                            >
                              {cat.name} {cat.count !== undefined && `(${cat.count})`}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              )}

              {categories.length > 0 && products.length > 0 && (
                <Divider sx={{ mx: 2, borderColor: BORDER_COLOR }} />
              )}

              {/* Products Group */}
              {products.length > 0 && (
                <Box sx={{ pt: 0.5, pb: 1.5, px: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '10px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: '#94a3b8',
                      letterSpacing: '0.08em',
                      mb: 0.25,
                    }}
                  >
                    PRODUCTS
                  </Typography>
                  <List disablePadding>
                    {products.map((prod) => (
                      <ListItemButton
                        key={prod.id}
                        onClick={() => handleProductClick(prod.id, prod.categorySlug)}
                        sx={{
                          py: 0.5,
                          px: 0.75,
                          borderRadius: '6px',
                          mb: 0.25,
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            bgcolor: '#f0f7ff',
                            '& .prod-name': { color: BRAND_LIGHT_BLUE },
                          },
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 38 }}>
                          <Avatar
                            variant="rounded"
                            src={prod.image}
                            sx={{
                              width: 28,
                              height: 28,
                              bgcolor: '#f8fafc',
                              border: `1px solid ${BORDER_COLOR}`,
                              borderRadius: '4px',
                              '& img': {
                                objectFit: 'contain',
                              },
                            }}
                          >
                            <DnsIcon sx={{ color: '#94a3b8', fontSize: 13 }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography
                              className="prod-name"
                              sx={{
                                fontFamily: FONT,
                                fontSize: '13px',
                                fontWeight: 600,
                                color: BRAND_BLUE,
                                transition: 'color 0.15s',
                              }}
                            >
                              {prod.name}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              sx={{
                                fontFamily: FONT,
                                fontSize: '11px',
                                color: '#64748b',
                              }}
                            >
                              {prod.category}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              )}

              {/* State: Nothing Found */}
              {categories.length === 0 && products.length === 0 && !loading && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '15px',
                      fontWeight: 600,
                      color: '#475569',
                      mb: 0.5,
                    }}
                  >
                    Nothing found
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '13px',
                      color: '#94a3b8',
                    }}
                  >
                    Try searching with other keywords.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* View More footer bar */}
            {totalCount > products.length && (
              <Box
                onClick={handleViewAll}
                sx={{
                  py: 1,
                  textAlign: 'center',
                  bgcolor: '#f8fafc',
                  borderTop: `1px solid ${BORDER_COLOR}`,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: '#f1f5f9',
                    '& p': { color: BRAND_BLUE },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: '13px',
                    fontWeight: 600,
                    color: BRAND_LIGHT_BLUE,
                  }}
                >
                  View More Results ({totalCount})
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default SearchModal;
